import React, { useState } from "react";
import {
    Typography,
    Box,
    TextField,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
} from "@mui/material";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { OrderShippingViewDTO, OrderShippingViewDTOFields } from "../../types/purchaseOrder/Order";
import { HttpShippings } from "../../http";
import { EntityWithIdFields } from "../../types/baseEntities";
import { mapsUrlBuilder } from "@ecommerce/shared-utils";
import { SHIPPING_ACTION_LINK_SX } from "../../styles/shippingActionLinkStyles";
import { BranchPickerDialog } from "../Shipping/BranchPickerDialog";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import {STYLES} from "../ShoppingCart/ShoppingCartStyles";




interface PickupOption {
  id: string;
  nombre: string;
  direccion: string;
  horarios: string[];
  costo: number;
}

interface ShippingCalculatorProps {
  onCalculateShipping: (zipCode: string) => void;
  onSelectShipping?: (cost: number) => void;
  productId: string;
  productVariantId?: string;
  quantity: number;

}

type Branch = {
    id: string;
    nombre?: string;
    direccion?: string;
    codigoPostal?: string;
    latitud?: string;
    longitud?: string;
    [key: string]: any;
};


const makeShippingValue = (shippingOptionId: string, branchId: string | null) =>
    `${shippingOptionId}::${branchId ?? ''}`;

const parseShippingValue = (value: string) => {
    const [shippingOptionId, branchIdRaw] = value.split('::');
    const branchId = branchIdRaw ? branchIdRaw : null;
    return { shippingOptionId, branchId };
};



export default function ShippingCalculator({ onCalculateShipping, onSelectShipping, productId, productVariantId, quantity }: ShippingCalculatorProps) {
  const [zipCode, setZipCode] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [shippingOptions, setShippingOptions] = useState<OrderShippingViewDTO[]>([]);
  const [selectedCost, setSelectedCost] = useState<number>(0);
  const [selectedFixedCost, setSelectedFixedCost] = useState(true);
  const [selectedIntegratedCost, setSelectedIntegratedCost] = useState(true);

  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

    const [branchDialogOpen, setBranchDialogOpen] = useState(false);
    const [branchDialogOptionId, setBranchDialogOptionId] = useState<string | null>(null);


    const getOptionById = (optionId: string) =>
        shippingOptions.find(o => o[EntityWithIdFields.Id] === optionId);

    const getBranchesForOption = (optionId: string): Branch[] => {
        const opt = getOptionById(optionId);
        return ((opt?.[OrderShippingViewDTOFields.Branches] ?? []) as Branch[]);
    };

    const openBranchDialog = (optionId: string) => {
        setBranchDialogOptionId(optionId);
        setBranchDialogOpen(true);
    };

    const closeBranchDialog = () => {
        setBranchDialogOpen(false);
        setBranchDialogOptionId(null);
    };


  // deprecado, no se cargan mas los envios de entrada, se calculan con el zipCode
  //useEffect(() => {
  //  HttpShippings.getShippingOptions().then(setShippingOptions).catch(console.error);
  //}, []);

  const homeOptions = shippingOptions.filter(o => o[OrderShippingViewDTOFields.RequestAddress] === true);
  const pickupOptions = shippingOptions.filter(o => o[OrderShippingViewDTOFields.RequestAddress] === false);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!zipCode.trim()) return;
        

        setSelectedOption("");
        setSelectedBranchId(null);
        setSelectedCost(0);
        setShippingOptions([]);
        setShowOptions(false);

        const payload = {
            codigoPostal: zipCode,
            productosOrden: [
                {
                    idProducto: productId,
                    idProductoVariante: productVariantId ?? "",
                    cantidad: quantity
                }
            ]
        };

        try {
            const options = await HttpShippings.calculateShippingOptions(payload);
            setShippingOptions(options);
            setShowOptions(true);
        } catch (err) {
            console.error("Error calculando envío:", err);
        }
    };


    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const { shippingOptionId, branchId } = parseShippingValue(value);

        const opt = getOptionById(shippingOptionId);
        const branches = ((opt?.[OrderShippingViewDTOFields.Branches] ?? []) as Branch[]);
        const isPickupWithBranches = (opt?.[OrderShippingViewDTOFields.RequestAddress] === false) && branches.length > 0;

        if (isPickupWithBranches && !branchId) {
            openBranchDialog(shippingOptionId);
            return;
        }

        applySelection(shippingOptionId, branchId);
    };



    const formatPrice = (price: number, fixedCost: boolean, integratedCost: boolean) => {

        if (!fixedCost && !integratedCost) { return " A convenir " }
        return price === 0 ? "Gratis" : `$${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const applySelection = (shippingOptionId: string, branchId: string | null) => {
        const selected = getOptionById(shippingOptionId);
        if (!selected) return;

        setSelectedOption(makeShippingValue(shippingOptionId, branchId));
        setSelectedBranchId(branchId);

        const cost = selected[OrderShippingViewDTOFields.Cost];
        const fixedCost = selected[OrderShippingViewDTOFields.FixedCost];
        const integratedCost = selected[OrderShippingViewDTOFields.IntegratedCost];

        setSelectedCost(cost);
        setSelectedFixedCost(fixedCost);
        setSelectedIntegratedCost(integratedCost);

        onSelectShipping?.(cost);
    };

    const dialogOption = branchDialogOptionId ? getOptionById(branchDialogOptionId) : null;
    const dialogBranches = branchDialogOptionId ? getBranchesForOption(branchDialogOptionId) : [];
    const dialogProviderName =
        (dialogOption?.[OrderShippingViewDTOFields.ProviderName] as string | undefined) ?? "";





  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          mb: 1,
          fontFamily: "'Rubik', sans-serif",
          fontSize: "10px",
          textTransform: "uppercase",
          color: "#010101",
        }}
      >
        MEDIOS DE ENVÍO
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", gap: 0, mb: 1 }}
      >
        <TextField
          placeholder="Tu código postal"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          size="small"
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              fontFamily: "'Rubik', sans-serif",
              fontSize: "12px",
              height: "40px",
              '& fieldset': {
                border: '1px solid #e0e0e0',
                borderRight: 'none',
              },
              '&:hover fieldset': {
                border: '1px solid #e0e0e0',
                borderRight: 'none',
              },
              '&.Mui-focused fieldset': {
                border: '1px solid #e0e0e0',
                borderRight: 'none',
              },
            },
          }}
        />
        <Button
          type="submit"
          variant="outlined"
          sx={BUTTON_STYLES.grey}
        >
          CALCULAR
        </Button>
      </Box>

      <Typography
        component="a"
        href="https://www.correoargentino.com.ar/formularios/cpa"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          ...TYPOGRAPHY_STYLES.rubikSmall,
          color: STYLES.colors.secondary,
          textDecoration: "none",
          cursor: "pointer",
          display: "block",
          mb: 3,
        }}
      >
        No sé mi código postal
      </Typography>

      {showOptions && (
        <RadioGroup value={selectedOption} onChange={handleOptionChange}>
          {/* Sección: Envío a domicilio */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: '#666' }} />
              <Typography
                sx={{
                  fontFamily: "'Rubik', sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#010101",
                }}
              >
                Envío a domicilio
              </Typography>
            </Box>

            {homeOptions.map((option, index) => (
              <Box key={option[EntityWithIdFields.Id]} sx={{ border: '1px solid #f0f0f0', mb: 1, p: 1 }}>
                <FormControlLabel
                  value={makeShippingValue(option[EntityWithIdFields.Id], null)}
                  control={
                    <Radio
                      sx={{
                        color: '#d0d0d0',
                        '&.Mui-checked': {
                          color: '#010101',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      width: '100%',
                      py: 1.5
                    }}>
                      <Box sx={{ flex: 1, pr: 2 }}>
                        <Typography
                          sx={{
                            fontFamily: "'Rubik', sans-serif",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#010101",
                            mb: 0.5,
                            lineHeight: 1.4,
                          }}
                        >
                          {option[OrderShippingViewDTOFields.OptionName]}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'Rubik', sans-serif",
                            fontSize: "12px",
                            color: "#666",
                            lineHeight: 1.4,
                          }}
                        >
                          {option[OrderShippingViewDTOFields.FormattedDescription]}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "'Rubik', sans-serif",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: option[OrderShippingViewDTOFields.Cost] === 0 ? "#4caf50" : "#010101",
                          whiteSpace: 'nowrap',
                          ml: 2,
                        }}
                      >
                        {formatPrice(option[OrderShippingViewDTOFields.Cost], option[OrderShippingViewDTOFields.FixedCost], option[OrderShippingViewDTOFields.IntegratedCost])}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    width: '100%',
                    m: 0,
                    alignItems: 'flex-start',
                    '& .MuiFormControlLabel-label': {
                      width: '100%',
                    },
                  }}
                />
              </Box>
            ))}
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RoomOutlinedIcon sx={{ fontSize: 18, color: '#666' }} />
              <Typography
                sx={{
                  fontFamily: "'Rubik', sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#010101",
                }}
              >
                Retirar por
              </Typography>
            </Box>

            {pickupOptions.map((option) => {
                const optionId = option[EntityWithIdFields.Id];
                const branches = (option[OrderShippingViewDTOFields.Branches] ?? []) as Branch[];
                const isPickupWithBranches =
                    option[OrderShippingViewDTOFields.RequestAddress] === false && branches.length > 0;

                const isSelectedThisOption = selectedOption.startsWith(`${optionId}::`) && !!selectedBranchId;
                const selectedBranch = isSelectedThisOption
                    ? branches.find(b => b.id === selectedBranchId) ?? null
                    : null;

                const radioValue = makeShippingValue(optionId, isSelectedThisOption ? selectedBranchId : null);

                if (!isPickupWithBranches) {
                    return (
                        <Box key={optionId} sx={{ border: '1px solid #f0f0f0', mb: 1, p: 1 }}>
                            <FormControlLabel
                                value={makeShippingValue(optionId, null)}
                                control={
                                    <Radio
                                        sx={{
                                            color: '#d0d0d0',
                                            '&.Mui-checked': { color: '#010101' },
                                            '& .MuiSvgIcon-root': { fontSize: 20 },
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', py: 1.5 }}>
                                        <Box sx={{ flex: 1, pr: 2 }}>
                                            <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 500, color: "#010101", mb: 0.5, lineHeight: 1.4 }}>
                                                {option[OrderShippingViewDTOFields.OptionName]}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "12px", color: "#666", lineHeight: 1.4 }}>
                                                {option[OrderShippingViewDTOFields.FormattedDescription]}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "14px", fontWeight: 500, color: option[OrderShippingViewDTOFields.Cost] === 0 ? "#4caf50" : "#010101", whiteSpace: 'nowrap', ml: 2 }}>
                                            {formatPrice(option[OrderShippingViewDTOFields.Cost], option[OrderShippingViewDTOFields.FixedCost], option[OrderShippingViewDTOFields.IntegratedCost])}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ width: '100%', m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { width: '100%' } }}
                            />
                        </Box>
                    );
                }

                const providerName = option[OrderShippingViewDTOFields.ProviderName];
                const selectedBranchMapsUrl = selectedBranch
                    ? mapsUrlBuilder.buildBranchUrl(providerName, selectedBranch)
                    : null;

                return (
                    <Box key={optionId} sx={{ border: '1px solid #f0f0f0', mb: 1, p: 1 }}>
                        <FormControlLabel
                            value={radioValue}
                            control={
                                <Radio
                                    sx={{
                                        color: '#d0d0d0',
                                        '&.Mui-checked': { color: '#010101' },
                                        '& .MuiSvgIcon-root': { fontSize: 20 },
                                    }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', py: 1.5 }}>
                                    <Box sx={{ flex: 1, pr: 2 }}>
                                        <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 500, color: "#010101", mb: 0.5, lineHeight: 1.4 }}>
                                            {option[OrderShippingViewDTOFields.OptionName]}
                                            {selectedBranch?.nombre ? ` - ${selectedBranch.nombre}` : ""}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                justifyContent: "space-between",
                                                gap: 1,
                                                mt: 0.5,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Rubik', sans-serif",
                                                    fontSize: "12px",
                                                    color: "#666",
                                                    lineHeight: 1.4,
                                                    flex: 1,
                                                    minWidth: 0, // clave para que corte bien en flex
                                                }}
                                            >
                                                {selectedBranch?.direccion ? selectedBranch.direccion : "Seleccioná una sucursal para retiro"}
                                            </Typography>

                                            {selectedBranchMapsUrl && (
                                                <Button
                                                    component="a"
                                                    href={selectedBranchMapsUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    startIcon={<RoomOutlinedIcon sx={{ fontSize: 16 }} />}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{
                                                        ...SHIPPING_ACTION_LINK_SX,
                                                        mt: 0,
                                                        flexShrink: 0, // evita que se achique/rompa
                                                    }}
                                                >
                                                    Ver en mapa
                                                </Button>
                                            )}
                                        </Box>


                                        <Button
                                            variant="text"
                                            startIcon={
                                                selectedBranch
                                                    ? <SwapHorizOutlinedIcon sx={{ fontSize: 16 }} />
                                                    : <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />
                                            }
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                openBranchDialog(optionId);
                                            }}
                                            sx={{ ...SHIPPING_ACTION_LINK_SX, mt: 1 }}
                                        >
                                            {selectedBranch ? "Cambiar sucursal" : "Elegir sucursal"}
                                        </Button>


                                    </Box>

                                    <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "14px", fontWeight: 500, color: option[OrderShippingViewDTOFields.Cost] === 0 ? "#4caf50" : "#010101", whiteSpace: 'nowrap', ml: 2 }}>
                                        {formatPrice(option[OrderShippingViewDTOFields.Cost], option[OrderShippingViewDTOFields.FixedCost], option[OrderShippingViewDTOFields.IntegratedCost])}
                                    </Typography>
                                </Box>
                            }
                            sx={{ width: '100%', m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { width: '100%' } }}
                        />
                    </Box>
                );
            })}



          </Box>
        </RadioGroup>
      )}

      {selectedOption && (
        <Typography sx={{ mt: 2, fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>
          Costo de envío: {formatPrice(selectedCost, selectedFixedCost, selectedIntegratedCost)}
        </Typography>
      )}

        <BranchPickerDialog
            open={branchDialogOpen}
            onClose={closeBranchDialog}
            optionName={dialogOption?.[OrderShippingViewDTOFields.OptionName] as string | undefined}
            providerName={dialogOption?.[OrderShippingViewDTOFields.ProviderName] as string | undefined}
            branches={dialogBranches as any}
            selectedBranchId={selectedBranchId}
            onSelect={(branchId) => {
                if (!branchDialogOptionId) return;
                applySelection(branchDialogOptionId, branchId);
                closeBranchDialog();
            }}
            searchPlaceholder="Buscar por nombre, dirección o CP"
        />


    </Box>
  );
}














